import { type ReactNode, useCallback, useState } from 'react'
import { Image, Space } from 'antd'
import {
  DownloadOutlined,
  LeftOutlined,
  RightOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  UndoOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { DeleteIcon } from '@/shared/ui/icons'
import { useRequest } from '@/shared/api'
import { Toast } from '@/shared/ui/organisms/toast'
import { Button } from '@/shared/ui/atoms/button'
import { DeleteConfirm } from '@/shared/ui/molecules/delete-confirm'
import pdf from 'assets/icons/pdf-logo.png'
import word from 'assets/icons/word-logo.png'
import excel from 'assets/icons/excel-logo.png'
import DocumentViewer from './document-viewer'
import {
  getFileExtension,
  isImageExtension,
  isPdfExtension,
  isWordExtension,
  isExcelExtension,
} from './utils'
import type { AttachmentFile, GroupImageViewerProps } from './types'
import './image-group-viewer.css'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

interface DocumentDrawerState {
  visible: boolean
  file: AttachmentFile | null
}

const CLOSED_DRAWER: DocumentDrawerState = { visible: false, file: null }

function getDocumentIconSrc(ext: string): string {
  if (isPdfExtension(ext)) return pdf
  if (isWordExtension(ext)) return word
  if (isExcelExtension(ext)) return excel
  return pdf
}

function triggerBlobDownload(url: string, filename: string): void {
  fetch(url)
    .then((response: any) => response.blob())
    .then((blob: any) => {
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      URL.revokeObjectURL(blobUrl)
      link.remove()
    })
    .catch(() => {
      // Silently handle download errors
    })
}

function triggerDirectDownload(path: string, filename: string): void {
  const link = document.createElement('a')
  link.href = path
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function GroupImageViewer({
  imagesList,
  dispatch,
  noneWidth = false,
  itemWidth = 80,
  itemHeight = 80,
  refetch,
  deletable = true,
  maxTags,
}: GroupImageViewerProps): ReactNode {
  const { t } = useTranslation()
  const { request } = useRequest()
  const [current, setCurrent] = useState(0)
  const [documentDrawer, setDocumentDrawer] =
    useState<DocumentDrawerState>(CLOSED_DRAWER)

  const onDownload = useCallback(() => {
    const file = imagesList[current]
    if (!file?.path) return
    triggerBlobDownload(file.path, file.fileOriginalName ?? 'file')
  }, [imagesList, current])

  const deleteAttachment = useMutation<any, any, any>({
    mutationFn: (id: string) =>
      request({ url: `/v1/attachments/${id}/delete`, method: 'DELETE' }),
  })

  const onConfirm = useCallback(
    (attached: AttachmentFile) => {
      const id = attached.id ?? attached.attachmentId
      if (!id) return

      deleteAttachment.mutate(id, {
        onSuccess: () => {
          const newData = imagesList.filter(
            (doc) =>
              doc.attachmentId !== attached.attachmentId ||
              doc.id !== attached.id
          )

          dispatch({ type: 'setDocs', payload: newData, own: 'true' })

          Toast({ type: 'success', message: 'Files successfully deleted !' })

          if (typeof refetch === 'function') {
            refetch()
          }
        },
        onError: (err: Error) => {
          Toast({ type: 'error', message: err.message })
        },
      })
    },
    [imagesList, dispatch, deleteAttachment, refetch]
  )

  const onCancel = useCallback(() => {
    Toast({ type: 'error', message: 'Action cancelled!' })
  }, [])

  const openDocumentDrawer = useCallback((file: AttachmentFile) => {
    setDocumentDrawer({ visible: true, file })
  }, [])

  const closeDocumentDrawer = useCallback(() => {
    setDocumentDrawer(CLOSED_DRAWER)
  }, [])

  const handleFileClick = useCallback(
    (e: React.MouseEvent, item: AttachmentFile) => {
      e.preventDefault()

      const ext = getFileExtension(item)
      const isImage = isImageExtension(ext) || isImageExtension(item.extension)

      if (isImage) return // Allow native Image.PreviewGroup to handle it

      const isDocument =
        isPdfExtension(ext) ||
        isWordExtension(ext) ||
        isExcelExtension(ext) ||
        isPdfExtension(item.extension) ||
        isWordExtension(item.extension) ||
        isExcelExtension(item.extension)

      if (isDocument) {
        openDocumentDrawer(item)
      } else if (item.path) {
        triggerDirectDownload(item.path, item.fileOriginalName ?? 'downloaded_file')
      }
    },
    [openDocumentDrawer]
  )

  const containerStyle: React.CSSProperties = {
    display: maxTags ? 'grid' : 'flex',
    ...(maxTags ? { gridTemplateColumns: `repeat(${maxTags}, 1fr)` } : {}),
  }

  return (
    <div
      className="glasspro-image-wrapper w-full gap-[3px] items-center justify-start text-left overflow-hidden overflow-x-auto"
      style={containerStyle}
    >
      <Image.PreviewGroup
        preview={{
          toolbarRender: (
            _: React.ReactNode,
            info: {
              transform: { scale: number }
              actions: {
                onActive?: (offset: number) => void
                onFlipY?: () => void
                onFlipX?: () => void
                onRotateLeft?: () => void
                onRotateRight?: () => void
                onZoomOut?: () => void
                onZoomIn?: () => void
                onReset?: () => void
              }
            }
          ) => (
            <Space className="glasspro-toolbar px-6 text-white text-xl bg-black/10 rounded-full">
              <LeftOutlined onClick={() => info.actions.onActive?.(-1)} />
              <RightOutlined onClick={() => info.actions.onActive?.(1)} />
              <DownloadOutlined onClick={onDownload} />
              <SwapOutlined rotate={90} onClick={info.actions.onFlipY} />
              <SwapOutlined onClick={info.actions.onFlipX} />
              <RotateLeftOutlined onClick={info.actions.onRotateLeft} />
              <RotateRightOutlined onClick={info.actions.onRotateRight} />
              <ZoomOutOutlined disabled={info.transform.scale === 1} onClick={info.actions.onZoomOut} />
              <ZoomInOutlined disabled={info.transform.scale === 50} onClick={info.actions.onZoomIn} />
              <UndoOutlined onClick={info.actions.onReset} />
            </Space>
          ),
          onChange: (index: number) => {
            setCurrent(index)
          },
        }}
      >
        {imagesList.map((item, index) => {
          const ext = getFileExtension(item)
          const isImage =
            isImageExtension(ext) || isImageExtension(item.extension)

          const imageSrc = isImage
            ? item.path
            : getDocumentIconSrc(ext || item.extension || '')

          return (
            <div key={item.id ?? item.attachmentId ?? index} style={{ position: 'relative' as const }}>
              <div
                onClick={!isImage ? (e: React.MouseEvent) => handleFileClick(e, item) : undefined}
                style={{ cursor: isImage ? 'default' : 'pointer' }}
              >
                <Image
                  preview={isImage}
                  src={imageSrc}
                  height={itemHeight}
                  width={itemWidth}
                  loading="lazy"
                  style={{ cursor: isImage ? 'default' : 'pointer' }}
                />
              </div>
              {deletable && (
                <div
                  style={{
                    position: 'absolute' as const,
                    top: 0,
                    right: 0,
                    color: 'white',
                    cursor: 'pointer',
                  }}
                  title={t('delete')}
                >
                  <DeleteConfirm
                    title={` ${item.name ?? item.uploadedFile?.fileOriginalName ?? ''}`}
                    onConfirm={() => onConfirm(item)}
                    onCancel={onCancel}
                  >
                    <DeleteIcon color="white" hovercolor="red" />
                  </DeleteConfirm>
                </div>
              )}
            </div>
          )
        })}
      </Image.PreviewGroup>

      <DocumentViewer
        file={documentDrawer.file}
        visible={documentDrawer.visible}
        onClose={closeDocumentDrawer}
      />
    </div>
  )
}

export default GroupImageViewer
